import { fileRequest, request } from '@/apis/http';
import { PlatformType } from '@/app/aio_admin/permission/apis';
import {
  CurrentPlatformUserEmployeeQueryReq,
  EmployeeUserVo,
  ExportTemplateVo,
  GlobalConfigReqUpdate,
  GlobalConfigSearchReq,
  GlobalConfigVo,
  MenuAllocationReq,
  MenuAllocationTreeVo,
  OperateLogDetailVo,
  OperateLogInfoVo,
  OperateLogQuery,
  SysMenuVo,
  TenantReq,
  TenantVo,
} from 'typings/openapi';
import { ExportFileByQueryRequest, ExportFiledType, GetExportFieldsRequest, GetGlobalConfigResponse } from './types';

/** 获取全局配置 */
export const GetGlobalConfig = (params?: { dictTypeList?: string[] }) => {
  return request<GetGlobalConfigResponse>('/globalConfig/info', { params });
};

/** 获取当前平台下的用户/员工 */
export const GetCurrentPlatformUserOrEmployee = (data: CurrentPlatformUserEmployeeQueryReq) => {
  return request<PagingResponse<EmployeeUserVo>>('/employee/currentPlatformUserEmployeeList', {
    method: 'POST',
    data,
  });
};

/** 根据租户ID获取全局配置 */
export const GetGlobalConfigByTenantId = (data: GlobalConfigSearchReq) => {
  return request<GlobalConfigVo>('/globalConfig/byTenantId/info', { data, method: 'POST' });
};

/** 更新全局配置 */
export const UpdateGlobalConfig = (data?: GlobalConfigReqUpdate) => {
  return request('/globalConfig/update', { data, method: 'PUT' });
};

/** 获取全局配置 */
export const GetMenuList = () => {
  return request<SysMenuVo[]>('/sysMenu/getMenuTree');
};

/** 获取菜单分配树 */
export const GetMenuAllocationTree = (platformType: PlatformType) => {
  return request<MenuAllocationTreeVo>(`/sysMenu/menuAllocationTree/${platformType}`, { method: 'POST' });
};

/** 保存菜单分配 */
export const SaveMenuAllocation = (platformType: PlatformType, data: MenuAllocationReq) => {
  return request(`/sysMenu/menuAllocation/save/${platformType}`, { data, method: 'POST' });
};
/** 日志分页列表 */
export const GetOperateLogList = (data: OperateLogQuery) => {
  return request<PagingResponse<OperateLogInfoVo>>('/operateLog/page', { data, method: 'POST' });
};
/** 日志详情 */
export const GetOperateLogDetail = (params: { logInfoId: number }) => {
  return request<OperateLogDetailVo[]>('/operateLog/detail', { params, method: 'GET' });
};

/** 数据初始化-清除数据 */
export const DataInitClear = (type: string) =>
  request(`/dataErasure/delete/${type}`, { method: 'GET', timeout: 60 * 1000 });

/** 需方初始化：清除当前需方的出入库单及库存台账，回退关联单据状态 */
export const ClearDataDemand = () => request('/dataErasure/initDemand', { method: 'POST', timeout: 60 * 1000 });
/** 供方初始化：清除当前供方的出入库单及库存台账，回退关联单据状态 */
export const ClearDataSupply = () => request('/dataErasure/initSupplier', { method: 'POST', timeout: 60 * 1000 });

/** 获取导出字段的列表 */
export const GetFieldsByTemplate = (params: GetExportFieldsRequest) => {
  return request<ExportTemplateVo>('/product/trendsExportTemplate', { params });
};

/** 获取基础配置 */
export const GetBaseConfig = () => {
  return request<TenantVo>('/tenant/info');
};
/** 更新基础配置 */
export const UpdateBaseConfig = (data: TenantReq) => {
  return request<TenantVo>('/tenant/update', { data, method: 'PUT' });
};

/**
 * 导出文件
 * @description 这是一个聚合接口
 * @todo 等后端接口统一后，把外部使用的参数进行修改
 */
export const ExportFileByQuery = (data: ExportFileByQueryRequest) => {
  /** 这里后端接口没有统一，需要聚合接口地址，参数也不统一 */
  const controlsType = data.isAll ? '1' : '0';
  const urlMap: Record<ExportFiledType, any> = {
    // 商品
    [ExportFiledType.PRODUCT]: {
      url: '/product/exportExcel',
      data: {
        idList: data.keys,
      },
    },
    // 需方
    [ExportFiledType.PURCHASE]: {
      url: '/demandInfo/export',
      data: {
        ids: data.keys,
      },
    },
    // 供方
    [ExportFiledType.SUPPLIER]: {
      url: '/supplier/export',
      data: {
        ids: data.keys,
      },
    },
    // 质检报告
    [ExportFiledType.QUALITY_INSPECTION_REPORT]: {
      url: '/report/export',
      data: {
        reportIds: data.keys,
      },
    },
    [ExportFiledType.PURCHASE_ORDER]: {
      url: '/purchase/exportExcel',
      data: {
        purchaseIdList: data.keys,
      },
    },
    [ExportFiledType.SUPPLY_PROFIT_ACCOUNTING_DETAIL_GROUP]: {
      url: '/profitCalculation/batchExport/detailGroup',
      data: {
        purchaseDetailIdList: data.keys?.map((k: any) => Number(k)),
      },
    },
    [ExportFiledType.SUPPLY_PROFIT_ACCOUNTING_DOCUMENTS_GROUP]: {
      url: '/profitCalculation/batchExport/documentsGroup',
      data: {
        purchaseNoList: data.keys?.map((k: any) => String(k)),
      },
    },
    [ExportFiledType.SUPPLY_PROFIT_ACCOUNTING_PRODUCT_GROUP]: {
      url: '/profitCalculation/batchExport/productGroup',
      data: {
        productSpecificationIdList: data.keys?.map((k: any) => Number(k)),
      },
    },
    // 应收汇总
    [ExportFiledType.RECEIVABLE_SUMMARY]: {
      url: '/receivablesSummary/export',
      data: {
        demandList: data.keys,
      },
    },
    // 应付汇总
    [ExportFiledType.PAYABLE_SUMMARY]: {
      url: '/summaryPayable/export',
      data: {
        supplierList: data.keys,
      },
    },
    // 进项核算
    [ExportFiledType.INPUT_ACCOUNTING]: {
      url: '/inputTax/export',
      data: {
        uniqueKeyList: data.keys,
      },
    },
    // 销项核算
    [ExportFiledType.OUTPUT_ACCOUNTING]: {
      url: '/outputTax/export',
      data: {
        uniqueKeyList: data.keys,
      },
    },
    // 计划到货明细
    [ExportFiledType.PLAN_DELIVERY_INFO]: {
      url: '/purchase/detail/summary/exportPlanExcel',
      data: {
        checkReq: data.keys,
      },
    },
    // 采购需求汇总
    [ExportFiledType.PURCHASE_REQUIRE_SUMMARY]: {
      url: '/purchaseSummary/supplier/exportPlanDeliveryProduct',
      data: {
        productSpecificationsIdList: data.keys,
      },
    },
    [ExportFiledType.SUPPLY_PROCUREMENT_SUMMARY]: {
      url: '/purchaseSummary/supplier/exportByProduct',
      data: {
        productSpecificationsIdList: data.keys,
      },
    },
    [ExportFiledType.PURCHASE_PROCUREMENT_SUMMARY]: {
      url: '/purchaseSummary/demand/exportByProduct',
      data: {
        productSpecificationsIdList: data.keys,
      },
    },
    [ExportFiledType.MANAGE_PROCUREMENT_SUMMARY]: {
      url: '/purchaseSummary/generating/exportByProduct',
      data: {
        productSpecificationsIdList: data.keys,
      },
    },

    [ExportFiledType.SUPPLY_PROCUREMENT_DEMAND_SUMMARY]: {
      url: '/purchaseSummary/supplier/exportByDemand',
      data: {
        demandList: data.keys,
      },
    },
    [ExportFiledType.PURCHASE_PROCUREMENT_DEMAND_SUMMARY]: {
      url: '/purchaseSummary/generating/exportByDemand',
      data: {
        demandList: data.keys,
      },
    },
    [ExportFiledType.SUPPLY_PROCUREMENT_SUPPLY_SUMMARY]: {
      url: '/purchaseSummary/demand/exportBySupplier',
      data: {
        supplierList: data.keys,
      },
    },
    [ExportFiledType.PURCHASE_PROCUREMENT_SUPPLY_SUMMARY]: {
      url: '/purchaseSummary/generating/exportBySupplier',
      data: {
        supplierList: data.keys,
      },
    },
    [ExportFiledType.PURCHASE_APPLY_GOODS_DETAIL]: {
      url: '/purchase/request/summary/generating/exportPurchaseRequestSummary',
      data: {
        productSpecificationsIdList: data.keys,
      },
    },
    [ExportFiledType.MANAGE_APPLY_PURCHASE_SUMMARY]: {
      url: '/purchase/request/summary/demand/exportPurchaseRequestProductList',
      data: {
        productSpecificationsIdList: data.keys,
      },
    },
    [ExportFiledType.MANAGE_APPLY_PURCHASE]: {
      url: '/purchaseRequest/generating/export',
      data: {
        purchaseRequestNoList: data.keys,
      },
    },
    [ExportFiledType.PURCHASE_APPLY_PURCHASE]: {
      url: '/purchaseRequest/demand/export',
      data: {
        purchaseRequestNoList: data.keys,
      },
    },
    [ExportFiledType.MANAGE_SUPPLY_PURCHASE_CONTRACT]: {
      url: '/pact/exportExcel',
      data: {
        ids: data.keys,
      },
    },
    [ExportFiledType.SupplyCommodity]: {
      url: '/product/notSuperVise/exportExcel',
      data: {
        idList: data.keys,
      },
    },
    [ExportFiledType.COMPOSE_PRODUCT]: {
      url: '/compose/product/exportExcel',
      data: {
        idList: data.keys,
      },
    },
    [ExportFiledType.PurchaserCookbook]: {
      url: '/menu/demand/export',
      data: {
        menuNos: data.keys,
      },
    },
    [ExportFiledType.ManageCookbook]: {
      url: '/menu/generating/export',
      data: {
        menuNos: data.keys,
      },
    },
    [ExportFiledType.COOKBOOK_SUMMARY]: {
      url: '/menu/statistics/export',
    },
    [ExportFiledType.ACCOUNT_MANAGE]: {
      url: '/demandAccount/export',
      data: {
        demandAccountId: data.keys,
      },
    },
    [ExportFiledType.ACCOUNT_TURNOVER]: {
      url: '/demandAccountRecords/export',
      data: {
        ids: data.keys,
      },
    },
    [ExportFiledType.INVENTORY_STOCK]: {
      url: '/stock/register/exportExcel',
      data: {
        exportSpecIds: data.keys,
      },
    },
    [ExportFiledType.SUPPLY_INVENTORY_STOCK]: {
      url: '/stock/register/exportExcel/supplier',
      data: {
        exportSpecIds: data.keys,
      },
    },
    [ExportFiledType.INVENTORY_REALTIME]: {
      url: '/stock/register/exportExcel/realTime',
      data: {
        exportSpecIds: data.keys,
      },
    },
    [ExportFiledType.SUPPLY_INVENTORY_REALTIME]: {
      url: '/stock/register/supplier/exportExcel/realTime',
      data: { exportSpecIds: data.keys },
    },
    [ExportFiledType.MANAGE_PRICE_MONITOR]: {
      url: '/quotation/exportPriceMonitor',
      data: {
        specificationsIdList: data.keys,
      },
    },
    [ExportFiledType.PURCHASE_PRICE_MONITOR]: {
      url: '/purchase/demand/exportPriceMonitor',
      data: {
        specificationsIdList: data.keys,
      },
    },
    [ExportFiledType.SUPPLY_PRICE_MONITOR]: {
      url: '/quotation/supplier/exportPriceMonitor',
      data: {
        specificationsIdList: data.keys,
      },
    },
    [ExportFiledType.INBOUND_ORDER]: {
      url: '/order/inbound/exportExcel',
      data: {
        inboundOrderNoList: data.keys,
      },
    },
    [ExportFiledType.OUTBOUND_ORDER]: {
      url: '/order/outbound/exportExcel',
      data: {
        outboundOrderNoList: data.keys,
      },
    },
    [ExportFiledType.REQUISITION_ORDER]: {
      url: '/order/apply/exportExcel',
      data: {
        applyOrderNoList: data.keys,
      },
    },
    [ExportFiledType.SUPPLY_REQUISITION_ORDER]: {
      url: '/order/apply/supplier/exportExcel',
      data: {
        applyOrderNoList: data.keys,
      },
    },
    [ExportFiledType.PURCHASE_STATEMENT]: {
      url: '/statement/demand/export',
      data: {
        statementNoList: data.keys,
      },
    },
    [ExportFiledType.SUPPLY_STATEMENT]: {
      url: '/statement/supplier/export',
      data: {
        statementNoList: data.keys,
      },
    },
    [ExportFiledType.MANAGE_STATEMENT]: {
      url: '/statement/regulation/export',
      data: {
        statementNoList: data.keys,
      },
    },
    [ExportFiledType.PAYMENT_STATEMENT]: {
      url: '/payment/export',
      data: {
        paymentNoList: data.keys,
      },
    },
    [ExportFiledType.PURCHASE_PAYMENT_STATEMENT]: {
      url: '/payment/export',
      data: {
        paymentNoList: data.keys,
      },
    },
    [ExportFiledType.SupplierPerformance]: {
      url: '/supplierEval/export',
      data: {
        ids: data.keys,
      },
    },
    [ExportFiledType.DISPATCH_INVENTORY]: {
      url: '/stock/register/exportExcel/dispatchStock',
      data: { exportSpecIds: data.keys },
    },
    [ExportFiledType.SUPPLIER_DISPATCH_INVENTORY]: {
      url: '/stock/register/supplier/exportExcel/dispatchStock',
      data: { exportSpecIds: data.keys },
    },
    [ExportFiledType.AIO_NUTRIENT]: {
      url: '/ingredientsNutrition/export',
      data: {
        ingredientsNameList: data.keys,
      },
    },
    [ExportFiledType.INVEBTORY_SHEET]: {
      url: '/takeStock/export',
      data: {
        tackStockNoList: data.keys,
      },
    },
    [ExportFiledType.MATERIAL_CONVERSION]: {
      url: '/material/convert/exportExcel',
      data: {
        materialConvertNoList: data.keys,
      },
    },
    [ExportFiledType.SUPPLY_MATERIAL_CONVERSION]: {
      url: '/material/convert/supplier/exportExcel',
      data: {
        materialConvertNoList: data.keys,
      },
    },
    [ExportFiledType.MANAGE_SUPPLY_BILL_RECORD]: {
      url: '/invoiceRecord/supervisorAndSupplier/export',
      data: {
        invoiceNoList: data.keys,
      },
    },
    [ExportFiledType.PURCHASE_BILL_RECORD]: {
      url: '/invoiceRecord/demand/export',
      data: { invoiceNoList: data.keys },
    },
    [ExportFiledType.REPLENISH_RULES]: {
      url: '/stock/register/exportExcel/supplement',
      data: { productSpecificationIdList: data.keys },
    },
    [ExportFiledType.INCOME_EXPENSE_RECORD]: {
      url: '/revenue/exportExcel/record',
      data: {
        recordNoList: data.keys,
      },
    },
    [ExportFiledType.VALIDITY_WARNING]: {
      url: '/stock/register/exportExcel/shelfLife',
      data: { productSpecificationIdList: data.keys },
    },
    [ExportFiledType.INWENTORY_WARNING]: {
      url: '/stock/register/exportExcel/inventory',
      data: { productSpecificationIdList: data.keys },
    },
    [ExportFiledType.TURN_OVER_BASKET_STATISTICS]: {
      url: '/turnoverBasket/record/statisticExport',
      data: { ids: data.keys },
    },
    [ExportFiledType.LIMIT_TIME]: {
      url: '/product/priceLimit/exportExcel',
      data: { productIds: data.keys },
    },
    [ExportFiledType.MEAL_SUMMARY]: {
      url: '/menu/label/exportExcel/mealSummary',
      data: { demandIdList: data.keys },
    },
    [ExportFiledType.PACKAGE_MANAGE]: {
      url: '/menu/label/exportExcel/fixed',
      data: { idList: data.keys },
    },
    [ExportFiledType.MEAL_RECORD]: {
      url: '/menu/record/exportExcel/record',
      data: { menuRecordNoList: data.keys },
    },
    [ExportFiledType.PACKAGE_DEMAND_SUMMARY]: {
      url: '/menu/record/exportExcel/summary',
      data: { exportMenuFixedInfoIds: data.keys },
    },
    [ExportFiledType.MANAGE_FINANCIAL_SUMMARY]: {
      url: '/financialSummary/supervisor/export/date',
      data: { dateList: data.keys },
    },
    [ExportFiledType.MANAGE_FINANCIAL_MONTH_SUMMARY]: {
      url: '/financialSummary/supervisor/export/month',
      data: { dateList: data.keys },
    },
    [ExportFiledType.SUPPLY_FINANCIAL_SUMMARY]: {
      url: '/revenue/statisticsResultByDay/supplierExport',
      data: { dateList: data.keys },
    },
    [ExportFiledType.SUPPLY_FINANCIAL_MONTH_SUMMARY]: {
      url: '/revenue/statisticsResultByMonth/supplierExport',
      data: { dateList: data.keys },
    },
    [ExportFiledType.PURCHASE_FINANCIAL_SUMMARY]: {
      url: '/revenue/statisticsResultByDay/export',
      data: { dateList: data.keys },
    },
    [ExportFiledType.PURCHASE_FINANCIAL_MONTH_SUMMARY]: {
      url: '/revenue/statisticsResultByMonth/export',
      data: { dateList: data.keys },
    },
    [ExportFiledType.TAX_RATE]: {
      url: '/product-tax/export',
      data: { idList: data.keys },
    },
    [ExportFiledType.UNIFORM_PRICE]: {
      url: '/uniform-pricing/uniformPricing/exportExcel',
      data: { idList: data.keys },
    },
    [ExportFiledType.DINE_PERSONNAL]: {
      url: '/diners/exportExcel',
      data: { idList: data.keys },
    },
    [ExportFiledType.DEAD_STOCK]: {
      url: '/productCategory/export/unsoldProduct',
      data: { productIds: data.keys },
    },
    [ExportFiledType.PAYMENT_RECEIPT]: {
      url: '/payment-slip/export',
      data: { idList: data.keys },
    },
    [ExportFiledType.PAYMENT_RECORDS]: {
      url: '/payment-slip-record/export',
      data: { idList: data.keys },
    },
    [ExportFiledType.MANAGE_PAYMENT_RECORDS]: {
      url: '/payment-slip-record/export/manage',
      data: { idList: data.keys },
    },
    [ExportFiledType.ACCOMPANY_SUMMARY]: {
      url: '/summary_meal/diners/export',
      data: { idList: data.keys },
    },
    [ExportFiledType.CLASS_SUMMARY]: {
      url: '/summary_meal/class/export',
      data: { idList: data.keys },
    },
    [ExportFiledType.EMPLOYEE_EXPORT]: {
      url: '/employee/base/data/export',
      data: { idList: data.keys },
    },
    [ExportFiledType.PURCHASER_DINING_CALENDAR]: {
      url: '/diningCalender/exportExcel',
      data: { idList: data.keys },
    },
    [ExportFiledType.MANAGE_DINING_CALENDAR]: {
      url: '/diningCalender/exportExcelForManage',
      data: { idList: data.keys },
    },
    [ExportFiledType.MANAGE_PURCHASE_SUMMARY_PRODUCT]: {
      url: '/purchaseSummary/generating/byProduct/export/v2',
      data: { productSpecificationsIdList: data.keys },
    },
    [ExportFiledType.MANAGE_PURCHASE_SUMMARY_DEMAND]: {
      url: '/purchaseSummary/generating/byDemand/export/v2',
      data: { demandIdList: data.keys },
    },
    [ExportFiledType.MANAGE_PURCHASE_SUMMARY_SUPPLIER]: {
      url: '/purchaseSummary/generating/bySupplier/export/v2',
      data: { supplierIdList: data.keys },
    },
    [ExportFiledType.AFTER_SAFES]: {
      url: '/afterSale/export',
      data: { afterSaleIdList: data.keys },
    },
    [ExportFiledType.ManageSelfCookbook]: {
      url: '/menu/generating/self/exportExcel',
      data: { menuNoList: data.keys },
    },
    [ExportFiledType.SUPPLY_PURCHASE_SUMMARY_DEMAND]: {
      url: '/purchaseSummary/supplier/exportPlanDeliveryDemand',
      data: { demandIdList: data.keys },
    },
    [ExportFiledType.SUPPLY_PURCHASE_SUMMARY_DINING]: {
      url: '/purchaseSummary/supplier/exportPlanDeliveryCanteen',
      data: { canteenIdList: data.keys },
    },
    [ExportFiledType.MANAGE_PURCHASE_SUMMARY_DINING]: {
      url: '/purchaseSummary/generating/byCanteen/export/v2',
      data: { canteenIdList: data.keys },
    },
    [ExportFiledType.PURCHASER_PORTRAIT]: {
      url: '/demandPortrait/export',
      data: { demandIdList: data.keys },
    },
    [ExportFiledType.PURCHASE_SKU_REMARK]: {
      url: '/purchase/product/remake/export',
      data: { detailIds: data.keys },
    },
    [ExportFiledType.PURCHASE_DETAIL_REPORT]: {
      url: '/purchase/detail/demand/export',
      data: { idList: data.keys },
    },
    [ExportFiledType.SUPPLY_DETAIL_REPORT]: {
      url: '/purchase/detail/suppler/export',
      data: { idList: data.keys },
    },
    [ExportFiledType.FOOD_SAFETY_REPORT]: {
      url: '/purchaseSummary/request/business/summary/export',
      data: { demandList: data.keys },
    },
    [ExportFiledType.DRIVER_PERFORMANCE]: {
      url: '/delivery/performance/export',
      data: { driverIdList: data.keys },
    },
    [ExportFiledType.MANAGE_PROCUREMENT_SUMMARY_BI]: {
      url: '/aio/date/summary/generating/exportByProduct',
      data: { productSpecificationsIdList: data.keys },
    },
    [ExportFiledType.MANAGE_PROCUREMENT_DEMAND_SUMMARY_BI]: {
      url: '/aio/date/summary/generating/exportByDemand',
      data: { demandList: data.keys },
    },
    [ExportFiledType.MANAGE_PROCUREMENT_SUPPLY_SUMMARY_BI]: {
      url: '/aio/date/summary/generating/exportBySupplier',
      data: { supplierList: data.keys },
    },
    [ExportFiledType.MANAGE_FINANCIAL_DATE_SUMMARY_BI]: {
      url: '/aio/date/summary/export/date',
      data: { dateList: data.keys },
    },
    [ExportFiledType.MANAGE_FINANCIAL_MONTH_SUMMARY_BI]: {
      url: '/aio/date/summary/export/month',
      data: { dateList: data.keys },
    },
    [ExportFiledType.PURCHASE_PROCUREMENT_SUMMARY_BI]: {
      url: '/aio/date/summary/demand/exportByProduct',
      data: { productSpecificationsIdList: data.keys },
    },
    [ExportFiledType.PURCHASE_PROCUREMENT_SUPPLY_SUMMARY_BI]: {
      url: '/aio/date/summary/demand/exportBySupplier',
      data: { supplierList: data.keys },
    },
    [ExportFiledType.SUPPLY_PROCUREMENT_SUMMARY_BI]: {
      url: '/aio/date/summary/supplier/exportByProduct',
      data: { productSpecificationsIdList: data.keys },
    },
    [ExportFiledType.SUPPLY_PROCUREMENT_DEMAND_SUMMARY_BI]: {
      url: '/aio/date/summary/supplier/exportByDemand',
      data: { demandList: data.keys },
    },
    [ExportFiledType.PURCHASE_FINANCIAL_DATE_SUMMARY_BI]: {
      url: '/aio/date/summary/supplier/exportByDemand',
      data: { dateList: data.keys },
    },
    [ExportFiledType.PURCHASE_FINANCIAL_MONTH_SUMMARY_BI]: {
      url: '/aio/date/summary/statisticsResultByMonth/export/demand',
      data: { dateList: data.keys },
    },

    [ExportFiledType.SUPPLY_FINANCIAL_DATE_SUMMARY_BI]: {
      url: '/aio/date/summary/statisticsResultByDay/export/supplier',
      data: { dateList: data.keys },
    },
    [ExportFiledType.SUPPLY_FINANCIAL_MONTH_SUMMARY_BI]: {
      url: '/aio/date/summary/statisticsResultByMonth/export/supplier',
      data: { dateList: data.keys },
    },
    [ExportFiledType.MANAGE_SAFE_SUMMARY_BI]: {
      url: '/aio/date/summary/business/summary/export',
      data: { demandList: data.keys },
    },
    [ExportFiledType.PURCHASE_STOCK_DETAIL_REPORT]: {
      url: '/purchase/detail/demand/register/export',
      data: { idList: data.keys },
    },
    [ExportFiledType.PURCHASE_GROUP_SUMMARY]: {
      url: '/demandSummary/supervisor/export',
      data: { idList: data.keys },
    },
    [ExportFiledType.PURCHASE_DAILY_PRICE]: {
      url: '/dailyPrice/export',
      data: { idList: data.keys },
    },
    [ExportFiledType.PLAN_DELIVERY_DETAIL_DINING]: {
      url: '/purchase/detail/summary/exportPlanExcel/canteen',
      data: { canteenIdList: data.keys },
    },
    [ExportFiledType.PLAN_DELIVERY_DETAIL_SUPPLIER]: {
      url: '/purchase/detail/summary/exportPlanExcel/supplier',
      data: { supplierIds: data.keys },
    },
    [ExportFiledType.PURCHASER_PURCHASE_SUMMARY_DINING]: {
      url: '/purchaseSummary/demand/exportByCanteen',
      data: { canteenIdList: data.keys },
    },
    [ExportFiledType.SUPPLY_PROCUREMENT_SUMMARY_DINING]: {
      url: '/purchaseSummary/supplier/exportByCanteen',
      data: { canteenIdList: data.keys },
    },
    [ExportFiledType.MANAGE_PROCUREMENT_SUMMARY_DINING]: {
      url: '/purchaseSummary/generating/exportByCanteen',
      data: { canteenIdList: data.keys },
    },
    [ExportFiledType.MANAGE_APPLY_PURCHASE_SUMMARY_DEMAND]: {
      url: '/purchase/request/summary/generating/exportPurchaseRequestByDemandSummary',
      data: { demandIdList: data.keys },
    },
    [ExportFiledType.MANAGE_APPLY_PURCHASE_SUMMARY_DINING]: {
      url: '/purchase/request/summary/generating/exportPurchaseRequestByCanteenSummary',
      data: { canteenIdList: data.keys },
    },
    [ExportFiledType.PURCHASER_APPLY_PURCHASE_SUMMARY_DINING]: {
      url: '/purchase/request/summary/demand/exportPurchaseRequestProductListByCanteen',
      data: { canteenIdList: data.keys },
    },
    [ExportFiledType.MANAGE_STATEMENT_SUMMARY]: {
      url: '/statementSummary/export',
      data: { exportDemandIdList: data.keys },
    },
    [ExportFiledType.MANAGE_PURCHASE_DIFF]: {
      url: '/purchaseDifference/export',
      data: { uniqueKeyList: data.keys },
    },
    [ExportFiledType.MANAGE_INSPECTION_DEMAND]: {
      url: '/inspection/statistics/export/by/demand',
      data: { demandIdList: data.keys },
    },
    [ExportFiledType.MANAGE_INSPECTION_SUPPLY]: {
      url: '/inspection/statistics/export/by/supplier',
      data: { supplierIdList: data.keys },
    },
    [ExportFiledType.SUPPLY_MODIFY_RECORD]: {
      url: '/inspection/history/by/supplier/export',
      data: { idList: data.keys },
    },
    [ExportFiledType.DEMAND_MODIFY_RECORD]: {
      url: '/inspection/history/by/demand/export',
      data: { idList: data.keys },
    },
    [ExportFiledType.MANAGE_PERFORMANCE_SCORE]: {
      url: '/supplierPerformanceScoreRecord/export',
      data: { supplierPerformanceScoreRecordNoList: data.keys },
    },
    [ExportFiledType.MANAGE_PURCHASE_SUMMARY_EVAL_BY_DATE]: {
      url: '/purchaseSummary/eval/generating/exportPurchaseSummaryEvalByDate',
      data: { exportDataList: data.keys },
    },
    [ExportFiledType.MANAGE_PURCHASE_SUMMARY_EVAL_BY_SUPPLIER]: {
      url: '/purchaseSummary/eval/generating/exportPurchaseSummaryEvalBySupplier',
      data: { exportDataList: data.keys },
    },
    [ExportFiledType.MANAGE_PURCHASE_SUMMARY_EVAL_BY_DEMAND]: {
      url: '/purchaseSummary/eval/generating/exportPurchaseSummaryEvalByDemand',
      data: { exportDataList: data.keys },
    },
    [ExportFiledType.PURCHASER_PURCHASE_SUMMARY_EVAL_BY_DATE]: {
      url: '/purchaseSummary/eval/demand/exportPurchaseSummaryEvalByDate',
      data: { exportDataList: data.keys },
    },
    [ExportFiledType.PURCHASER_PURCHASE_SUMMARY_EVAL_BY_SUPPLIER]: {
      url: '/purchaseSummary/eval/demand/exportPurchaseSummaryEvalBySupplier',
      data: { exportDataList: data.keys },
    },
    [ExportFiledType.SUPPLY_PURCHASE_SUMMARY_EVAL_BY_DATE]: {
      url: '/purchaseSummary/eval/supplier/exportPurchaseSummaryEvalByDate',
      data: { exportDataList: data.keys },
    },
    [ExportFiledType.SUPPLY_PURCHASE_SUMMARY_EVAL_BY_DEMAND]: {
      url: '/purchaseSummary/eval/supplier/exportPurchaseSummaryEvalByDemand',
      data: { exportDataList: data.keys },
    },
    [ExportFiledType.MANAGE_AFTER_SALE_DIFF]: {
      url: '/afterSale/difference/export',
      data: { idList: data.keys },
    },
    [ExportFiledType.PURCHASE_AFTER_SALE_DETAIL]: {
      url: '/afterSale/detail/summary/demand/export',
      data: { idList: data.keys },
    },
    [ExportFiledType.SUPPLY_AFTER_SALE_DETAIL]: {
      url: '/afterSale/detail/summary/supplier/export',
      data: { idList: data.keys },
    },
    [ExportFiledType.MANAGE_STATEMENT_DETAIL_PRODUCT]: {
      url: '/statementDetails/productGroupingSupervisionExport',
      data: { keyList: data.keys },
    },
    [ExportFiledType.MANAGE_STATEMENT_DETAIL_CATEGORY]: {
      url: '/statementDetails/productCategoryGroupingSupervisionExport',
      data: { keyList: data.keys },
    },
    [ExportFiledType.MANAGE_STATEMENT_DETAIL_BILL]: {
      url: '/statementDetails/documentsGroupingSupervisionExport',
      data: { keyList: data.keys },
    },
    [ExportFiledType.SUPPLY_DETAIL_REPORT_PRODUCT]: {
      url: '/statementDetails/productGroupingSupplierExport',
      data: { keyList: data.keys },
    },
    [ExportFiledType.SUPPLY_DETAIL_REPORT_CATEGORY]: {
      url: '/statementDetails/productCategoryGroupingSupplierExport',
      data: { keyList: data.keys },
    },
    [ExportFiledType.SUPPLY_DETAIL_REPORT_BILL]: {
      url: '/statementDetails/documentsGroupingSupplierExport',
      data: { keyList: data.keys },
    },
    [ExportFiledType.PURCHASER_DETAIL_REPORT_PRODUCT]: {
      url: '/statementDetails/productGroupingDemandExport',
      data: { keyList: data.keys },
    },
    [ExportFiledType.PURCHASER_DETAIL_REPORT_CATEGORY]: {
      url: '/statementDetails/productCategoryGroupingDemandExport',
      data: { keyList: data.keys },
    },
    [ExportFiledType.PURCHASER_DETAIL_REPORT_BILL]: {
      url: '/statementDetails/documentsGroupingDemandExport',
      data: { keyList: data.keys },
    },
    [ExportFiledType.DRIVE_DATA]: {
      url: '/device/history/travel/export',
      data: { timeList: data.keys },
    },
    [ExportFiledType.PLATFORM_DOCKING_DEMAND_SYNC_CONFIG]: {
      url: '/thirdPartySyncDemand/export',
      data: { demandIdList: data.keys },
    },
    [ExportFiledType.PLATFORM_DOCKING_SUPPLIER_SYNC_CONFIG]: {
      url: '/thirdPartySyncSupplier/export',
      data: { supplierIdList: data.keys },
    },
    [ExportFiledType.PLATFORM_DOCKING_SUPERVISE_PRODUCT_SYNC_CONFIG]: {
      url: '/thirdPartySyncProduct/export',
      data: { productSpecificationIdList: data.keys },
    },
    [ExportFiledType.GUANMAI_DEMAND_SYNC_CONFIG]: {
      url: '/guanmaiDemand/export',
      data: { demandIdList: data.keys },
    },
    [ExportFiledType.GUANMAI_SUPERVISE_PRODUCT_SYNC_CONFIG]: {
      url: '/guanmaiProduct/export/supervise',
      data: { productSpecificationIdList: data.keys },
    },
    [ExportFiledType.GUANMAI_NON_SUPERVISE_PRODUCT_SYNC_CONFIG]: {
      url: '/guanmaiProduct/export/nonSupervise',
      data: { productSpecificationIdList: data.keys },
    },
    [ExportFiledType.VEHICLE_ALARM]: {
      url: '/alarm/vehicle/export',
      data: { idList: data.keys },
    },
    [ExportFiledType.SUPPLY_COST_RECORD]: {
      url: '/costRecording/batchExport',
      data: { purchaseDetailIdList: data.keys },
    },
    [ExportFiledType.OUTSOURCE_TASK_LIST_DETAIL]: {
      url: '/outsourcedTasks/batchExport/detailGroup',
      data: { purchaseDetailIdList: data.keys },
    },
    [ExportFiledType.OUTSOURCE_TASK_LIST_BILL]: {
      url: '/outsourcedTasks/batchExport/documentsGroup',
      data: { purchaseNoList: data.keys },
    },
    [ExportFiledType.OUTSOURCE_TASK_LIST_PRODUCT]: {
      url: '/outsourcedTasks/batchExport/productGroup',
      data: { productSpecificationIdList: data.keys },
    },
    [ExportFiledType.OUTSOURCE_INCOME_LIST]: {
      url: '/outsourcedIncome/batchExport',
      data: { batchSupplierIdList: data.keys },
    },
    [ExportFiledType.REPLACE_SORTING_LIST]: {
      url: '/purchaseHelpSorting/batchExport',
      data: { purchaseNoList: data.keys },
    },
    [ExportFiledType.MANAGE_IO_RECORD]: {
      url: '/device/io/report/export/generate',
      data: { uniqueIdList: data.keys },
    },
    [ExportFiledType.SUPPLY_IO_RECORD]: {
      url: '/device/io/report/export/supplier',
      data: { uniqueIdList: data.keys },
    },
    // 行车报警-监管方
    [ExportFiledType.MANAGE_VEHICLE_ALARM]: {
      url: '/device/io/Alarm/export/generate',
      data: { uniqueIdList: data.keys },
    },
    // 行车报警-供方
    [ExportFiledType.SUPPLY_VEHICLE_ALARM]: {
      url: '/device/io/Alarm/export/supplier',
      data: { uniqueIdList: data.keys },
    },
    // 收据
    [ExportFiledType.RECEIPT]: {
      url: '/platformReceipt/export',
      data: {
        platformReceiptNoList: data.keys,
      },
    },
    [ExportFiledType.MANAGE_PRICE_REVIEW]: {
      url: '/pactReview/exportGenerating',
      data: {
        pactSidList: data.keys,
      },
    },
    [ExportFiledType.PURCHASE_PRICE_REVIEW]: {
      url: '/pactReview/exportDemand',
      data: {
        pactSidList: data.keys,
      },
    },
    [ExportFiledType.MANAGE_PRODUCT_REVIEW]: {
      url: '/acceptanceReview/exportGenerating',
      data: {
        detailIdList: data.keys,
      },
    },
    [ExportFiledType.PURCHASE_PRODUCT_REVIEW]: {
      url: '/acceptanceReview/exportDemand',
      data: {
        detailIdList: data.keys,
      },
    },
    [ExportFiledType.SUPPLY_SOURCE_LIST]: {
      url: '/supplySource/export',
      data: {
        ids: data.keys,
      },
    },
    // 货源绩效
    [ExportFiledType.SUPPLY_SOURCE_PERFORMANCE]: {
      url: '/supplySource/performanceExport',
      data: {
        ids: data.keys,
      },
    },
    [ExportFiledType.SUPPLY_REPLENISHMENT_ORDER]: {
      url: '/replenishment/export',
      data: {
        replenishmentNoList: data.keys,
      },
    },
    // 补货成本
    [ExportFiledType.SUPPLY_REPLENISHMENT_COST]: {
      url: '/replenishmentCost/export',
      data: { supplySourceIdList: data.keys },
    },
    // 补货进度
    [ExportFiledType.SUPPLY_REPLENISHMENT_PROGRESS]: {
      url: '/replenishmentProgress/export',
      data: { specIdList: data.keys },
    },
    [ExportFiledType.SUPPLY_REPLENISH_RULES]: {
      url: '/stock/register/supplier/exportExcel/supplement',
      data: {
        productSpecificationIdList: data.keys,
      },
    },
    [ExportFiledType.OUTSOURCE_ACCEPTANCE_LIST]: {
      url: '/outsourcedAcceptance/batchExport',
      data: { costRecordingIdList: data.keys },
    },
    [ExportFiledType.SHELF_LIFE_RECORD]: {
      url: '/shelfLifeRecord/export',
      data: { ids: data.keys },
    },
    [ExportFiledType.SHELF_LIFE_TRACK]: {
      url: '/shelfLifeRecord/stockExport',
      data: { productSpecificationIds: data.keys },
    },
    [ExportFiledType.MANAGE_FINANCIAL_COMPLIANCE_REVIEW]: {
      url: '/financialComplianceReview/exportGenerating',
      data: { purchaseNoList: data.keys },
    },
    [ExportFiledType.PURCHASE_FINANCIAL_COMPLIANCE_REVIEW]: {
      url: '/financialComplianceReview/exportDemand',
      data: { purchaseNoList: data.keys },
    },
    [ExportFiledType.TURN_OVER_BASKET_DELIVERY]: {
      url: '/turnoverBasketDelivery/export',
      data: { recordNoList: data.keys },
    },
    // 行情价汇聚（新发地）：filter 携带 prodName/categoryId/pubDateStart/pubDateEnd
    [ExportFiledType.XINFADI_PRICE]: {
      url: '/xinfadiPrice/export',
      data: { ids: data.keys },
    },
    // 库存损耗表-按单据导出
    [ExportFiledType.INVENTORY_LOSS_ORDER]: {
      url: '/stockLossReport/exportByOrder',
      data: { detailIdList: data.keys },
    },
    // 库存损耗表-按商品导出
    [ExportFiledType.INVENTORY_LOSS_PRODUCT]: {
      url: '/stockLossReport/exportByProduct',
      data: { productSpecificationIdList: data.keys },
    },
    // 配送损耗表-按单据导出
    [ExportFiledType.DELIVERY_LOSS_ORDER]: {
      url: '/deliveryLossReport/exportByOrder',
      data: { detailIdList: data.keys },
    },
    // 配送损耗表-按商品导出
    [ExportFiledType.DELIVERY_LOSS_PRODUCT]: {
      url: '/deliveryLossReport/exportByProduct',
      data: { productSpecificationIdList: data.keys },
    },
    // 供方分拣任务
    [ExportFiledType.SUPPLY_SORT_TASK_LIST]: {
      url: '/supplierSortTask/export',
      data: { productSpecificationIdList: data.keys },
    },
    [ExportFiledType.SUPPLY_REPLENISHMENT_PRICE]: {
      url: '/agreementPrice/export',
      data: { agreementPriceNoList: data.keys },
    },
    // 库位分配规则
    [ExportFiledType.POSITION_RULE]: {
      url: '/stock/stock/locationRule/export',
      data: { ids: data.keys },
    },
    // 监管方-资金记录
    [ExportFiledType.MANAGE_FUND_RECORD_LIST]: {
      url: '/fundRecords/exportSupervision',
      data: { fundRecordsNoList: data.keys },
    },
    // 需方-资金记录
    [ExportFiledType.PURCHASE_FUND_RECORD_LIST]: {
      url: '/fundRecords/export',
      data: { fundRecordsNoList: data.keys },
    },
    // 记账凭证
    [ExportFiledType.VOUCHER]: {
      url: '/financeVoucher/export',
      data: { voucherNoList: data.keys },
    },
    // 报价单
    [ExportFiledType.QUOTATION]: {
      url: '/quotation/export',
      data: { quotationNoList: data.keys },
    },
  };

  const query = urlMap[data.type];

  return fileRequest(query.url, {
    method: 'POST',
    data: { controlsType, exportStr: data.exportStr, ...data.filter, ...query.data },
  });
};
